// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — controllers for the balance/mechanism sims (real game logic, not shipped into the live
// hot path). Each returns the next Action (take/draw) to apply.
//
//  · SmartBot replays the board's constructed solution → clears any cairn, using only the draws
//    the solution needs. It proves every cairn is solvable and hoards draws (the reference).
//  · GreedyBot is careless: it impatiently DRAWS a third of the time instead of hunting the chain,
//    so it wastes the budget. It survives Ramble (a soft end, never a loss) but BURIES in Scree —
//    the sign flip that proves Scree's draw budget is load-bearing, not decoration.
//  · BlindBot draws at random and only stumbles onto a legal take — it reads nothing, so it almost
//    never clears a cairn. The control that shows reading the runes is load-bearing.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import { canDraw, canTake, exposedIds, legalTakes, type GameState } from './game';
import type { Action } from './board';

export interface Controller {
  next(s: GameState): Action | null;
}

/** Replays the board's own full-clear solution — completes any cairn with the fewest draws. */
export class SmartBot implements Controller {
  private idx = -1;
  private queue: Action[] = [];
  next(s: GameState): Action | null {
    if (s.index !== this.idx) {
      this.idx = s.index;
      this.queue = s.board.solution.slice();
    }
    return this.queue.shift() ?? null;
  }
}

/**
 * Careless play: impatiently draws a third of the time even when a legal take exists, otherwise
 * takes a RANDOM legal stone (no chain planning). It wastes draws — harmless where there is no
 * budget (Ramble), fatal where there is one (Scree).
 */
export class GreedyBot implements Controller {
  private rng: Rng;
  constructor(seed: number) {
    this.rng = makeRng((seed ^ 0x5bce7f21) >>> 0);
  }
  next(s: GameState): Action | null {
    const legal = legalTakes(s);
    const canD = canDraw(s);
    if (canD && this.rng() < 0.35) return { k: 'draw' }; // impatient — burns the budget
    if (legal.length) return { k: 'take', id: legal[Math.floor(this.rng() * legal.length) % legal.length] };
    if (canD) return { k: 'draw' };
    return null;
  }
}

/** Random flailing: draws half the time, otherwise only stumbles onto a legal exposed stone. */
export class BlindBot implements Controller {
  private rng: Rng;
  constructor(seed: number) {
    this.rng = makeRng((seed ^ 0x1a2b3c4d) >>> 0);
  }
  next(s: GameState): Action | null {
    const canD = canDraw(s);
    if (canD && this.rng() < 0.5) return { k: 'draw' };
    const exp = exposedIds(s);
    if (exp.length) {
      const id = exp[Math.floor(this.rng() * exp.length) % exp.length];
      if (canTake(s, id)) return { k: 'take', id };
    }
    if (canD) return { k: 'draw' };
    const legal = legalTakes(s);
    if (legal.length) return { k: 'take', id: legal[Math.floor(this.rng() * legal.length) % legal.length] };
    return null;
  }
}
